import { useEffect, useState, type FormEvent } from "react";
import { useChangeRole, useCreateUser, useResetPassword, useToggleUser, useUsers, type Users } from "../../api/admin";
import { formatDateTime } from "../../api/dates";
import { StatusChip } from "../../components/ui";
import { AdminDrawer, ListSearch, matches } from "./AdminDrawer";
import AdminLayout from "./AdminLayout";
import { VisibilityMatrix } from "./VisibilityMatrix";

/** Users: every login, its role, and the guardrails around both. You cannot
 * lock yourself out or brick the last office login; the server refuses and
 * the sentence is shown. Below it, the role x screen visibility matrix. */
export default function UsersPage() {
  const users = useUsers();
  const [creating, setCreating] = useState(false);
  return (
    <AdminLayout
      title="Users"
      sub="Sign-ins and roles"
      actions={
        users.data?.may_administer === false ? null : (
          <button type="button" className="btn btn-primary" onClick={() => setCreating(true)} disabled={!users.data}>
            + Create a login
          </button>
        )
      }
    >
      {users.isError ? <p className="notice bad">The logins could not be loaded: {users.error.message}</p> : null}
      {users.isPending ? <div className="table-card empty-state">Loading logins…</div> : null}
      {users.data ? <UsersView data={users.data} creating={creating} onCloseCreate={() => setCreating(false)} /> : null}
    </AdminLayout>
  );
}

export function UsersView({
  data,
  creating = false,
  onCloseCreate = () => undefined,
}: {
  data: Users;
  creating?: boolean;
  onCloseCreate?: () => void;
}) {
  const toggle = useToggleUser();
  const reset = useResetPassword();
  const changeRole = useChangeRole();
  const [notice, setNotice] = useState<string | null>(data.notice);
  const [query, setQuery] = useState("");
  useEffect(() => setNotice(data.notice), [data.notice]);
  const error = toggle.error?.message ?? reset.error?.message ?? changeRole.error?.message ?? null;
  const rows = data.rows.filter((row) => matches(query, [row.email, row.provider, row.role]));
  return (
    <>
      {notice ? <p className="notice ok">{notice}</p> : null}
      {error ? <p className="notice bad">{error}</p> : null}
      {data.may_administer ? null : (
        <p className="notice">
          Only an administrator can change logins, roles or which screens each role is shown. Ask someone whose
          role includes the Administration screen.
        </p>
      )}
      <ListSearch value={query} onChange={setQuery} placeholder="Search email" />
      <div className="table-card">
        <table className="data-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Role</th>
              <th>Provider</th>
              <th>Last signed in</th>
              <th>Status</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <UserRowView
                key={row.id}
                row={row}
                data={data}
                onToggle={() => toggle.mutate(row.id)}
                onReset={(p) => reset.mutate({ id: row.id, password: p })}
                onRole={(role, provider) => changeRole.mutate({ id: row.id, role, provider_id: provider })}
              />
            ))}
            {rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="empty-state">
                  No login matches “{query}”.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      <VisibilityMatrix data={data} />
      {creating && data.may_administer ? (
        <AdminDrawer title="Create a login" onClose={onCloseCreate}>
          <CreateUser data={data} onDone={onCloseCreate} />
        </AdminDrawer>
      ) : null}
    </>
  );
}

function roleLabel(data: Users, role: string): string {
  return data.roles.find((r) => r.value === role)?.label ?? role;
}

function UserRowView({
  row,
  data,
  onToggle,
  onReset,
  onRole,
}: {
  row: Users["rows"][number];
  data: Users;
  onToggle: () => void;
  onReset: (password: string | null) => void;
  onRole: (role: string, providerId: number | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [role, setRole] = useState(row.role);
  const [provider, setProvider] = useState(row.provider_id === null ? "" : String(row.provider_id));
  const [password, setPassword] = useState("");
  return (
    <>
      <tr className={open ? "selected" : undefined}>
        <td>
          <span className="mono">{row.email}</span>
          {row.is_me ? <span className="muted"> (you)</span> : null}
        </td>
        <td>{roleLabel(data, row.role)}</td>
        <td>{row.provider ?? "—"}</td>
        <td>{row.last_login_at ? formatDateTime(row.last_login_at) : "never"}</td>
        <td>
          <StatusChip tone={row.active ? "ok" : "quiet"}>{row.active ? "Active" : "Inactive"}</StatusChip>
        </td>
        <td className="num">
          {data.may_administer ? (
            <button type="button" className="row-button" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
              {open ? "Close" : "Manage"}
            </button>
          ) : null}
        </td>
      </tr>
      {open ? (
        <tr className="manage-row">
          <td colSpan={6}>
            <div className="manage-grid">
              <button
                type="button"
                className={row.active ? "btn btn-danger" : "btn"}
                disabled={row.is_me}
                title={row.is_me ? "You cannot deactivate your own login" : undefined}
                onClick={onToggle}
              >
                {row.active ? "Deactivate" : "Reactivate"}
              </button>
              <div className="manage-group">
                <label className="field">
                  New password (blank generates one)
                  <input type="text" value={password} onChange={(e) => setPassword(e.target.value)} />
                </label>
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    onReset(password.trim() || null);
                    setPassword("");
                  }}
                >
                  Reset password
                </button>
              </div>
              <div className="manage-group">
                <label className="field">
                  Role
                  <select value={role} onChange={(e) => setRole(e.target.value)}>
                    {data.roles.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </label>
                {role === "provider" ? (
                  <label className="field">
                    Provider
                    <select value={provider} onChange={(e) => setProvider(e.target.value)}>
                      <option value="">Choose…</option>
                      {data.providers.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
                <button
                  type="button"
                  className="btn"
                  disabled={role === "provider" && provider === ""}
                  onClick={() => onRole(role, provider === "" ? null : Number(provider))}
                >
                  Change role
                </button>
              </div>
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function CreateUser({ data, onDone }: { data: Users; onDone: () => void }) {
  const create = useCreateUser();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("office");
  const [provider, setProvider] = useState("");
  const [password, setPassword] = useState("");
  function submit(e: FormEvent) {
    e.preventDefault();
    create.mutate(
      { email: email.trim(), role, provider_id: provider === "" ? null : Number(provider), password: password.trim() || null },
      { onSuccess: onDone },
    );
  }
  return (
    <form className="admin-form" onSubmit={submit}>
      <label className="field">
        Email
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
      </label>
      <label className="field">
        Role
        <select value={role} onChange={(e) => setRole(e.target.value)}>
          {data.roles.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
      </label>
      {role === "provider" ? (
        <label className="field">
          Provider
          <select value={provider} onChange={(e) => setProvider(e.target.value)} required>
            <option value="">Choose…</option>
            {data.providers
              .filter((p) => p.active)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
          </select>
        </label>
      ) : null}
      <label className="field">
        Password (blank generates one)
        <input type="text" value={password} onChange={(e) => setPassword(e.target.value)} />
      </label>
      {create.isError ? <p className="notice bad">{create.error.message}</p> : null}
      <div className="admin-form-actions">
        <button type="submit" className="btn btn-primary" disabled={create.isPending}>
          Create
        </button>
      </div>
    </form>
  );
}
